import { api } from "@/lib/api";
import axios from "axios";
import type { TipoIdentificacion } from "../../Types/types";

export interface LoginPayload {
    correo: string;
    clave: string;
    numero_identificacion: string;
    tipo_identificacion: string;
}

export interface LoginResponse {
    userId: string;
    type: string;
    name: string;
}

export interface MeResponse {
    user: {
        data: Record<string, unknown>;
    };
}

/**
 * Llamada al endpoint de login
 */
export const loginRequest = async (payload: LoginPayload) => {
    const { data } = await api.post<LoginResponse>("/api/auth/login", payload);
    return data;
};

/**
 * Obtener datos completos del usuario autenticado
 */
export const fetchCurrentUser = async () => {
    const { data } = await api.get<MeResponse>("/api/auth/me");
    return data;
};

/**
 * Obtener tipos de identificación desde la API
 */
export const fetchTiposIdentificacion = async (): Promise<TipoIdentificacion[]> => {
    const { data } = await api.get(
        "/api/dashboards/mis_datos/selects/tipos_identificacion"
    );

    if (data && data.data && Array.isArray(data.data)) {
        return data.data;
    } else if (data && Array.isArray(data)) {
        return data;
    } else {
        console.error("Formato de datos inesperado:", data);
        return [];
    }
};

/**
 * Mapea errores de la API a mensajes amigables para el usuario
 */
export const mapApiErrorToMessage = (status?: number, payload?: unknown): string => {
    const str = payload ? String(payload).toLowerCase() : "";

    if (
        status === 401 ||
        str.includes("invalid credentials") ||
        (str.includes("invalid") && str.includes("credentials"))
    ) {
        return "Correo o contraseña incorrectos. Por favor verifica tus datos.";
    }
    if (status === 404 || str.includes("not found") || str.includes("no encontrado")) {
        return "No se encontró una cuenta con esos datos. Verifica o regístrate.";
    }
    if (status === 423 || str.includes("locked") || str.includes("bloquead")) {
        return "Tu cuenta está bloqueada. Contacta con soporte.";
    }
    if (status && status >= 500) {
        return "Error en el servidor. Intenta más tarde.";
    }

    if (Array.isArray(payload)) {
        return payload.slice(0, 3).join(", ");
    }
    if (payload && typeof payload === "object" && payload !== null) {
        const errorObj = payload as Record<string, unknown>;

        if (errorObj.error && typeof errorObj.error === "string") {
            const m = errorObj.error.toLowerCase();
            if (m.includes("invalid") || m.includes("contraseña") || m.includes("correo")) {
                return "Datos ingresados inválidos. Revísalos e intenta de nuevo.";
            }
            return errorObj.error;
        }
        if (errorObj.message && typeof errorObj.message === "string") {
            return errorObj.message;
        }
        if (Array.isArray(errorObj.errors) && errorObj.errors.length) {
            return errorObj.errors.slice(0, 2).map((it) => String(it)).join(" - ");
        }
    }

    return "Error al iniciar sesión. Verifica tus datos e intenta nuevamente.";
};

/**
 * Extrae un mensaje amigable de un error axios o genérico
 */
export const handleLoginError = (err: unknown): string => {
    if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const payload = err.response?.data ?? err.message ?? err.toString();
        return mapApiErrorToMessage(status, payload);
    }
    return "Error del servidor. Intenta de nuevo más tarde.";
};

/**
 * Limpia un valor del usuario (quita puntos sueltos, nulls, etc.)
 */
export const cleanUserValue = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value !== "string") return String(value);
    const trimmed = value.trim();
    return trimmed === "." ? "" : trimmed;
};

/**
 * Limpia y arma el nombre del usuario desde la respuesta de login
 */
export const buildCleanName = (rawName?: string): string => {
    return (
        rawName
            ?.split(" ")
            .map((p: string) => (p.trim() === "." ? "" : p.trim()))
            .filter((p: string) => Boolean(p))
            .join(" ")
            .trim() || "Usuario"
    );
};

/**
 * Mapea los datos crudos del usuario desde /me a un objeto limpio
 */
export const buildMappedUser = (rawUser: Record<string, unknown>) => {
    const cleanedUser = Object.fromEntries(
        Object.entries(rawUser).map(([key, value]) => [key, cleanUserValue(value)])
    );

    return {
        userId: cleanedUser.idpaciente,
        type: "paciente",
        name: `${cleanedUser.primer_nombre} ${cleanedUser.segundo_nombre} ${cleanedUser.primer_apellido} ${cleanedUser.segundo_apellido}`
            .replace(/\s+/g, " ")
            .trim(),
        ...cleanedUser,
    };
};
